/**
 * Decide when to request the large plate sprite (3000px long edge).
 *
 * Standard plate sprites are 1500px on the long edge. To avoid upscaling blur,
 * we compare the viewport long edge * devicePixelRatio to an effective size
 * threshold. This prevents high-DPI small devices (e.g., 320px @ 3x) from
 * forcing the large sprite, while still upgrading on large/high-DPI screens.
 */
export const LARGE_SPRITE_THRESHOLD = 1800

export function prefersLargePlateSprite(): boolean {
  const devicePixelRatio = window.devicePixelRatio || 1
  const viewportLongEdge = Math.max(window.innerWidth, window.innerHeight)
  const effectiveLongEdge = viewportLongEdge * devicePixelRatio

  // Use large sprite when the effective long edge would upscale the 1500px
  // standard sprite noticeably.
  return effectiveLongEdge >= LARGE_SPRITE_THRESHOLD
}

/**
 * Decide when to request the large well/montage sprite.
 *
 * Well sprites support zoom/pan and are often inspected more closely, so when
 * zoom is enabled we bias toward larger assets by applying a zoom multiplier.
 *
 * @param options.viewportLongEdge Optional explicit long-edge size to use
 *   (e.g., measured container). Defaults to viewport long edge.
 * @param options.devicePixelRatio Optional explicit DPR. Defaults to window DPR.
 * @param options.zoomEnabled If true, apply the zoomMultiplier. Defaults false.
 * @param options.zoomMultiplier Multiplier to apply when zoomEnabled is true.
 *   Defaults to 2 (i.e., target 2x the viewport for zoom use-cases).
 */
export function prefersLargeMontageSprite(options?: {
  viewportLongEdge?: number
  devicePixelRatio?: number
  zoomEnabled?: boolean
  zoomMultiplier?: number
}): boolean {
  const devicePixelRatio = options?.devicePixelRatio ?? window.devicePixelRatio ?? 1
  const viewportLongEdge =
    options?.viewportLongEdge ?? Math.max(window.innerWidth, window.innerHeight)
  const zoomMultiplier =
    options?.zoomEnabled === true ? (options?.zoomMultiplier ?? 2) : 1

  const effectiveLongEdge = viewportLongEdge * devicePixelRatio * zoomMultiplier
  return effectiveLongEdge >= LARGE_SPRITE_THRESHOLD
}
